import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  Container,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import Notification from "../../../../../components/mui/Notification";
import { Assistant } from "../../../../../components";
import debounce from "lodash/debounce";

export const History = ({ caseStudy, handleUpdatingStatus }) => {
  const caseStudyId = caseStudy.id;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState();
  const [values, setValues] = useState({
    text: "",
  });

  useEffect(() => {
    caseStudyService
      .companyHistory(caseStudyId)
      .then((res) => {
        setValues({
          id: res.data?.id,
          text: res.data?.text,
          bclAssistantUsed: res.data?.createdByLLM,
        });
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not get company history. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const updateCompanyHistory = useCallback(
    (id, name, value) => {
      handleUpdatingStatus(true);

      const request = {
        caseStudyId,
        id: id,
        [name]: value,
      };
      caseStudyService
        .updateCompanyHistory(request)
        .then((res) => {
          setValues((prevValues) => ({
            ...prevValues,
            id: res.data,
            [name]: value,
          }));
        })
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update company history. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [caseStudyId, handleUpdatingStatus]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedSave = useMemo(
    () =>
      debounce((id, name, value) => {
        updateCompanyHistory(id, name, value);
      }, 2000),
    [updateCompanyHistory]
  );

  const createHistory = () => {
    setCreating(true);
    handleUpdatingStatus(true);
    const request = {
      id: values.id,
      caseStudyId,
      lang: caseStudy.inputParams?.lang,
    };
    caseStudyService
      .createCompanyHistory(request)
      .then((res) => {
        setValues({
          ...values,
          id: res.data.id,
          text: res.data.text,
          bclAssistantUsed: true,
        });
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not fetch company history. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setCreating(false);
        handleUpdatingStatus(false);
      });
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
    const { id } = values;

    setValues({
      ...values,
      [name]: value,
    });

    debouncedSave(id, name, value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Notification notify={notify} setNotify={setNotify} />
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 3 } }}>
          <Grid
            container
            spacing={0}
            justify="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item>
              <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
                Company History
              </Typography>
            </Grid>
            <Grid item style={{ marginLeft: "auto" }}>
              <Assistant
                disabled={creating || loading || values.bclAssistantUsed}
                onClick={createHistory}
                loading={creating}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loading || creating ? (
              <Skeleton variant="rounded" sx={{ fontSize: "10rem" }} />
            ) : (
              <TextField
                name="text"
                multiline
                fullWidth
                value={values.text}
                onChange={handleChanges}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};
